import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { ExtensionPanel } from './ExtensionPanel'
import { Ga4Card } from './Ga4Card'
import { SearchConsoleCard } from './SearchConsoleCard'

/**
 * Connections — S-D3's panel, and the screen where "connect LinkedIn" becomes
 * one thing again.
 *
 * **The whole reason this panel exists in this shape.** A tenant thinks they
 * have *a LinkedIn connection*. AugRank has two mechanisms with nothing in
 * common: an OAuth grant that lets it post (S-B17), and a browser extension that
 * lets it search from the tenant's own session (S-B14). They are granted
 * differently, they fail differently, and either can be missing while the other
 * works. Putting them on separate screens would mean a tenant whose posts go out
 * and whose discovery does not — or the reverse — having no single place that
 * says so. So the LinkedIn card carries both halves, and each one says plainly
 * what it does and does not cover.
 *
 * **The extension panel is rendered as a card inside this one**, which is what
 * `panels.ts` said would happen when Connections arrived. `/settings/extension`
 * still resolves — it redirects here — because notification emails sent before
 * this release carry that path.
 *
 * **Reconnect is the ordinary case, not an error path.** A LinkedIn access token
 * lasts sixty days and a Share on LinkedIn app is issued no refresh token, so
 * every tenant who keeps using the product comes back through the connect
 * button. The card says *when* it expires rather than waiting for a post to
 * fail, and `expiring` is true ten minutes early so the prompt is never late.
 *
 * **The risk acknowledgement is a checkbox that gates the button, and the server
 * does not rely on it.** Pressing Connect *is* the acknowledgement — the route
 * records it before the browser leaves — so the checkbox is here to make sure
 * nobody presses it without reading, not to carry a flag.
 *
 * The three states every component ships (CLAUDE.md, Frontend): loading while
 * the connection is read, an error state with a retry, and — the one that
 * matters most here — the empty state, which is what a freshly provisioned
 * tenant actually sees and is written as an invitation rather than as a warning.
 */

/** Mirrors `_ERRORS` in `api/app/v1/connections.py`: what the outcome query means. */
const OUTCOMES: Record<string, { tone: 'success' | 'danger'; title: string; body: string }> = {
  connected: {
    tone: 'success',
    title: 'LinkedIn connected',
    body: 'AugRank can now publish posts to the account you authorised.',
  },
  failed: {
    tone: 'danger',
    title: 'LinkedIn did not finish connecting',
    body:
      'Nothing was changed. This usually means the consent screen was cancelled, or it was ' +
      'left open too long. Try again — it takes a few seconds.',
  },
}

export function ConnectionsPanel(): JSX.Element {
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  // The Google grant, read once for the two cards built on it. It was the
  // Search Console card's own query until S-G13 added the GA4 card over the
  // same grant — two copies of one connection let a Disconnect on one card
  // leave the other still showing a credential that had just been deleted.
  const google = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/google', { signal })),
    [],
  )
  const now = new Date()

  const linkedin = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/linkedin', { signal })),
    [],
  )

  const start = useMutation(() => call(() => api.POST('/api/v1/connections/linkedin/start', {})))

  const disconnect = useMutation(() => call(() => api.DELETE('/api/v1/connections/linkedin', {})))

  // The callback redirects back here with `?linkedin=connected|failed`, because
  // a redirect from LinkedIn carries no body of ours. Read once, shown as a
  // toast, and then stripped from the URL — a reload should not re-announce a
  // connection that happened ten minutes ago.
  const outcome = params.get('linkedin')
  useEffect(() => {
    if (outcome === null) return
    const message = OUTCOMES[outcome]
    if (message !== undefined) {
      toast.show({ kind: message.tone, title: message.title, body: message.body })
    }
    params.delete('linkedin')
    setParams(params, { replace: true })
    linkedin.refetch()
    // `outcome` is the only input that should re-run this; re-running it on a
    // refetched connection would show the toast again on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  const connection = linkedin.state.data
  // Pulled out of the JSX because the generated type is `string | null |
  // undefined` — the field is optional on the schema — and narrowing it inline
  // would leave `relativeTime` taking a possibly-undefined argument.
  const expiresAt = connection?.expires_at ?? null
  const pages = connection?.pages ?? []

  return (
    <div className="ar-stack">
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>LinkedIn</h3>
          <span className="ar-card__spacer" />
          {connection?.connected === true ? (
            <span className="ar-badge ar-badge--success">✓ Connected</span>
          ) : connection?.paused === true ? (
            <span className="ar-badge ar-badge--danger">Paused</span>
          ) : connection?.expiring === true ? (
            <span className="ar-badge ar-badge--warning">⚠ Reconnect needed</span>
          ) : null}
        </div>

        {/*
          `.ar-card__body` carries the inset. Without it the prose starts at the
          card's border while the heading above it starts a `--ar-space-6` in,
          so the two disagree about where the card's left edge is.
        */}
        <div className="ar-card__body">
          <p className="ar-text-body-sm">
            Two separate permissions, and you can have one without the other.{' '}
            <strong>Posting</strong> uses LinkedIn&rsquo;s own authorisation, below.{' '}
            <strong>Finding leads</strong> runs in your own browser through the AugRank extension,
            in the card underneath. Neither one reads your LinkedIn password or session.
          </p>

          {linkedin.state.status === 'loading' ? (
            <Loading label="Loading your LinkedIn connection" />
          ) : linkedin.state.status === 'error' ? (
            <ErrorState failure={linkedin.state.failure} onRetry={linkedin.refetch} />
          ) : connection === null ? (
            // `success` with no body. Not reachable through the API — the route
            // always answers a `LinkedInConnectionOut` — and rendered as the empty
            // state rather than crashed on, because a panel that throws is a worse
            // answer to an impossible response than one that says "not connected".
            <p className="ar-empty ar-empty--inline">
              We could not read your LinkedIn connection just now. Reload the page to try again.
            </p>
          ) : (
            <>
              <InlineError failure={start.state.failure ?? disconnect.state.failure} />

              {connection.connected ? (
                <>
                  <p className="ar-text-body-sm">
                    Publishing as{' '}
                    <strong>{connection.account_label ?? 'your LinkedIn account'}</strong>
                    {expiresAt !== null
                      ? ` · authorisation ends ${relativeTime(expiresAt, now)}`
                      : null}
                    .
                  </p>
                  {connection.expiring ? (
                    <p className="ar-callout ar-text-body-sm" data-tone="warning">
                      This authorisation has run out or is about to. Reconnect now and nothing you
                      have queued will be missed — LinkedIn asks you to renew it every sixty days.
                    </p>
                  ) : null}
                  {pages.length > 0 ? (
                    <p className="ar-text-body-sm ar-muted">
                      Company Pages you can publish to: {pages.map((page) => page.name).join(', ')}.
                    </p>
                  ) : null}
                  <p className="ar-text-body-sm ar-muted">
                    AugRank publishes at most {String(connection.max_posts_per_day)} posts a day to
                    this account, spaced out through the day. {String(connection.posts_today)} used
                    today, {String(connection.posts_remaining_today)} left.
                  </p>
                  {/*
                    S-G9. The compose screen's entry point, and the only one:
                    `outreach.html#post` is not one of S-D3's seven Outreach
                    sections, so it has no sidebar row and this card is how it
                    is found. Offered only while the connection is usable —
                    a link to a composer that would refuse every press is worse
                    than no link, and the button above is what fixes it.
                  */}
                  <div>
                    <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/outreach/post">
                      Write a post
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="ar-empty ar-empty--inline">
                    {connection.status === null
                      ? 'LinkedIn is not connected, so AugRank cannot publish anything to it yet.'
                      : connection.paused
                        ? 'LinkedIn interrupted this account. Clear whatever it is asking for in your own browser, then resume in the card below.'
                        : 'This authorisation is no longer accepted by LinkedIn. Connect again to keep publishing.'}
                  </p>
                  {connection.detail !== null ? (
                    <p className="ar-text-body-sm ar-muted">LinkedIn said: {connection.detail}</p>
                  ) : null}
                </>
              )}

              {!connection.connected || connection.expiring ? (
                <>
                  {/*
                  The §8 acknowledgement. Shown every time the button is offered,
                  including on a reconnect, because the version recorded is the
                  version of the text that was on screen — and a reconnect that
                  skipped it would record an acknowledgement of something the
                  member was not shown that day.
                */}
                  <label className="ar-choice ar-choice--check ar-text-body-sm">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(event) => {
                        setAcknowledged(event.target.checked)
                      }}
                    />
                    {/*
                    `.ar-choice` hides the native input and draws this box in its
                    place, so the span is the control as far as the eye is
                    concerned and the input is still the control as far as the
                    keyboard and the accessibility tree are concerned. It has to
                    be the input's immediate next sibling — that adjacency is
                    what carries `:checked` and `:focus-visible` across.
                  */}
                    <span className="ar-choice__box" aria-hidden="true" />
                    <span>
                      I understand that automating LinkedIn is against its User Agreement, and that
                      LinkedIn can restrict or permanently ban the account AugRank acts as.
                    </span>
                  </label>
                  <Button
                    busy={start.pending}
                    busyLabel="Opening LinkedIn"
                    disabled={!acknowledged}
                    onClick={() => {
                      void (async () => {
                        const result = await start.run(undefined)
                        if (!result.ok) return
                        // A full navigation, not a fetch: the consent screen is
                        // LinkedIn's page and has to be rendered by the browser.
                        // The `state` cookie the server just set travels with it.
                        window.location.assign(result.data.url)
                      })()
                    }}
                  >
                    {connection.connected ? 'Reconnect LinkedIn' : 'Connect LinkedIn'}
                  </Button>
                </>
              ) : (
                <div>
                  <Button
                    tone="ghost"
                    size="sm"
                    onClick={() => {
                      setConfirmingDisconnect(true)
                    }}
                  >
                    Disconnect
                  </Button>
                </div>
              )}

              {connection.acknowledged_version !== null ? (
                <p className="ar-text-body-sm ar-muted">
                  Risk acknowledged · version {connection.acknowledged_version}
                </p>
              ) : null}
            </>
          )}
        </div>
      </section>

      {/*
        S-B14's panel, now a card inside this one. It carries the pause banner
        and the resume button, which is why the pause notification points at
        this route: a challenge stops posting and searching together.
      */}
      <ExtensionPanel />

      {/*
        S-B22a. The second connection, and its own component rather than more of
        this file — the two share a shape and nothing else, and its docstring
        argues the split. It handles its own `?search_console=` outcome, which is
        why the two toasts cannot collide.
      */}
      <SearchConsoleCard connection={google} />

      {/*
        S-G13, audit row §G28. Fourth rather than fifth, and directly under the
        card it depends on: it is the *same* Google grant seen from the other
        side, and a tenant reading "reconnect on the card above" should not have
        to hunt for which card that is.
      */}
      <Ga4Card connection={google} />

      {/*
        S-G5. The publish target's entry point, and a link rather than a fourth
        form: the configuration is **per site** and this panel is per
        organisation, so the screen itself lives at `/seo/publish-targets` with
        a site picker. What belongs here is discoverability — `configured_target`
        refuses a publish with *"Choose one in Settings, Publishing"*, and this
        is the card that sentence means. It reads nothing, so it cannot fail.
      */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Where we publish</h3>
        </div>
        <div className="ar-card__body">
          <p className="ar-text-body-sm">
            Approved articles are pushed to your own website — a git repository, WordPress, Webflow
            or Shopify. The address and the credential are set per site, because an agency running
            two domains needs two answers.
          </p>
          <p className="ar-text-body-sm ar-muted">
            Credentials for this are held in AWS Parameter Store as encrypted strings, never in
            AugRank&rsquo;s database and never shown again once saved.
          </p>
          <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/seo/publish-targets">
            Set up publishing
          </Link>
        </div>
      </section>

      {confirmingDisconnect ? (
        <Modal
          title="Disconnect LinkedIn?"
          onClose={() => {
            setConfirmingDisconnect(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirmingDisconnect(false)
                }}
              >
                Keep it connected
              </Button>
              <Button
                tone="danger"
                busy={disconnect.pending}
                busyLabel="Disconnecting"
                onClick={() => {
                  void (async () => {
                    const result = await disconnect.run(undefined)
                    setConfirmingDisconnect(false)
                    if (!result.ok) return
                    setAcknowledged(false)
                    linkedin.refetch()
                    toast.show({ kind: 'success', title: 'LinkedIn disconnected' })
                  })()
                }}
              >
                Disconnect
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            AugRank will delete the authorisation LinkedIn gave it and will stop publishing to this
            account. Anything already queued will not go out.
          </p>
          <p>
            Finding leads through the browser extension is <strong>not</strong> affected — that is a
            separate permission, in the card below.
          </p>
        </Modal>
      ) : null}
    </div>
  )
}
